import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { FirebaseAuthContainer } from "../../common/containers/auth/firebase-auth-container.util";
import { LoaderView } from "../loader/loader.comp";
import { parseAuthCallback } from "../../common/utilities/auth-domain/auth-url.util";
import { registerFirebase } from "../../api/auth/endpoints-referrers";
import { AxiosError } from "axios";
import { updateUser } from "../../api/users/endpoints-referrers";

/**
 * Handles the authentication callback from the auth domain
 * Extracts tokens from URL, stores them, loads user data, and redirects
 */
const AuthCallbackHandler = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { setTokens, loadUser } = FirebaseAuthContainer.useContainer();
    const [error, setError] = useState<string>("");
    const [isProcessing, setIsProcessing] = useState<boolean>(true);

    useEffect(() => {
        // Prevent multiple executions
        let executed = false;

        const processCallback = async () => {
            if (executed) return;
            executed = true;

            try {
                console.log("[AuthCallback] Processing callback...");
                console.log("[AuthCallback] URL:", window.location.href);

                // Parse auth callback parameters
                const {
                    token,
                    refresh,
                    success,
                    error: authError
                } = parseAuthCallback(searchParams);

                console.log("[AuthCallback] Parsed params:", {
                    hasToken: !!token,
                    hasRefresh: !!refresh,
                    success,
                    error: authError,
                    isOAuth: searchParams.get("isOAuth"),
                    email: searchParams.get("email"),
                    invite: searchParams.get("invite"),
                    firebaseUid: searchParams.get("firebaseUid")
                });

                // Check for authentication failure
                if (!success || authError) {
                    console.log("[AuthCallback] Authentication failed:", authError);
                    setError(authError || "Authentication failed");
                    setIsProcessing(false);
                    return;
                }

                // Validate tokens are present
                if (!token || !refresh) {
                    console.log("[AuthCallback] Missing tokens");
                    setError("Missing authentication tokens");
                    setIsProcessing(false);
                    return;
                }

                // Get OAuth-specific parameters
                const isOAuth = searchParams.get("isOAuth") === "true";
                const oauthEmail = searchParams.get("email");
                const invite = searchParams.get("invite");
                const firebaseUid = searchParams.get("firebaseUid");

                // Store tokens in localStorage first
                // This triggers loadUserData useEffect, but it will handle 422 gracefully
                console.log("[AuthCallback] Storing tokens...");
                setTokens({ accessToken: token, refreshToken: refresh });
                console.log("[AuthCallback] Tokens stored");

                // For OAuth users with invite codes, register them
                // The registerFirebase call needs tokens to be set for authentication
                if (isOAuth && invite && oauthEmail && firebaseUid) {
                    console.log("[AuthCallback] Registering OAuth user with invite...");
                    try {
                        // D7: companyName/forceCreateKB left the register
                        // contract with self-service signup.
                        const signupData = {
                            email: oauthEmail,
                            from: invite,
                            firebaseId: firebaseUid
                        };
                        await registerFirebase(signupData);
                        console.log("[AuthCallback] OAuth user registered successfully");
                    } catch (error) {
                        // If user already exists (409), update their firebaseId
                        if (error instanceof AxiosError && error.response?.status === 409) {
                            console.log("[AuthCallback] User exists, updating firebaseId...");
                            await updateUser(firebaseUid);
                            console.log("[AuthCallback] FirebaseId updated");
                        } else {
                            console.error("[AuthCallback] OAuth registration failed:", error);
                            // Don't throw - try to load user anyway in case they already exist
                        }
                    }
                }

                // Load user data from backend
                // The backend returns emailVerified from Firebase token claims
                console.log("[AuthCallback] Loading user data...");
                await loadUser();
                console.log("[AuthCallback] User data loaded successfully");

                // Clear tokens from URL immediately (security)
                // Use replace: true to prevent tokens from staying in browser history
                console.log("[AuthCallback] Navigating to home...");
                navigate("/", { replace: true });
            } catch (err: any) {
                console.error("[AuthCallback] Error processing auth callback:", err);
                console.error("[AuthCallback] Error details:", {
                    message: err.message,
                    response: err.response?.data,
                    status: err.response?.status
                });
                setError(err.message || "Failed to complete authentication");
                setIsProcessing(false);
            }
        };

        processCallback();
    }, [searchParams, setTokens, loadUser, navigate]);

    // Show error if authentication callback failed
    if (error) {
        return (
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100vh",
                    padding: "20px",
                    textAlign: "center"
                }}
            >
                <div
                    style={{
                        marginBottom: "20px",
                        fontSize: "24px",
                        fontWeight: 600,
                        color: "#d32f2f"
                    }}
                >
                    Authentication Error
                </div>
                <div
                    style={{
                        marginBottom: "30px",
                        fontSize: "16px",
                        color: "#666",
                        maxWidth: "500px",
                        lineHeight: "1.6"
                    }}
                >
                    {error}
                </div>
                <div style={{ fontSize: "14px", color: "#999" }}>
                    Please try logging in again. If the problem persists, contact support.
                </div>
            </div>
        );
    }

    // Show loading indicator while processing
    return <LoaderView show={isProcessing} />;
};

export default AuthCallbackHandler;
